import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// Latest-message-per-thread (drives the unread dot on inbox rows). Sharetribe's
// inquiry process has only ONE transition, so lastTransitionedAt does NOT move
// on a new message — the only reliable "new message" signal is the newest
// MESSAGE timestamp per thread, compared against the user's per-thread
// last-seen time (stored in currentUser.privateData.inboxLastSeen).
export const FETCH_LATEST_MESSAGES_REQUEST = 'app/InboxPage/FETCH_LATEST_MESSAGES_REQUEST';
export const FETCH_LATEST_MESSAGES_SUCCESS = 'app/InboxPage/FETCH_LATEST_MESSAGES_SUCCESS';
export const FETCH_LATEST_MESSAGES_ERROR = 'app/InboxPage/FETCH_LATEST_MESSAGES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  // Map of txId -> { latestMessageAt: <ISO string>, latestSenderId: <uuid> }.
  // Only the newest message per thread is stored (used for the unread dot).
  latestMessages: {},
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case FETCH_LATEST_MESSAGES_REQUEST:
      // Reset the map for a fresh inbox load so stale rows can't linger.
      return { ...state, latestMessages: {} };
    case FETCH_LATEST_MESSAGES_SUCCESS:
      return { ...state, latestMessages: payload || {} };
    case FETCH_LATEST_MESSAGES_ERROR:
      // Non-fatal: the inbox still renders, just without unread dots.
      console.error(payload); // eslint-disable-line
      return { ...state, latestMessages: {} };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchLatestMessagesRequest = () => ({ type: FETCH_LATEST_MESSAGES_REQUEST });
const fetchLatestMessagesSuccess = map => ({
  type: FETCH_LATEST_MESSAGES_SUCCESS,
  payload: map,
});
const fetchLatestMessagesError = e => ({
  type: FETCH_LATEST_MESSAGES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      // Best-effort (client-only): fetch the newest message per thread so the
      // inbox can show an unread dot. Never block the inbox render on it.
      const txs = response?.data?.data || [];
      dispatch(fetchLatestMessages(txs));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

// For each transaction, fetch ONLY the newest message (perPage: 1) so the inbox
// can determine which threads have an unread incoming message. This runs
// client-side only (skipped during SSR) and is fully non-fatal — any failure
// just means no unread dots that load. At the current inbox page size (10) this
// is a small, bounded number of tiny queries.
export const fetchLatestMessages = transactions => (dispatch, getState, sdk) => {
  // SSR guard — this is a pure UI enhancement; don't add message queries to the
  // server render path.
  if (typeof window === 'undefined') {
    return Promise.resolve({});
  }
  const txs = Array.isArray(transactions) ? transactions : [];
  if (txs.length === 0) {
    dispatch(fetchLatestMessagesSuccess({}));
    return Promise.resolve({});
  }

  dispatch(fetchLatestMessagesRequest());

  const fetchOne = tx => {
    const txId = tx?.id;
    const txIdUuid = txId?.uuid;
    if (!txIdUuid) return Promise.resolve(null);
    return sdk.messages
      .query({
        transaction_id: txId,
        include: ['sender'],
        'fields.message': ['createdAt', 'content'],
        page: 1,
        perPage: 1,
      })
      .then(res => {
        const msg = res?.data?.data?.[0];
        if (!msg) return null;
        const latestMessageAt = msg?.attributes?.createdAt
          ? new Date(msg.attributes.createdAt).toISOString()
          : null;
        // sender relationship id (only trust a real uuid string; never fall
        // back to an object, which would break the ownership === comparison).
        const senderRel = msg?.relationships?.sender?.data;
        const rawSenderId = senderRel?.id?.uuid || senderRel?.id;
        const latestSenderId = typeof rawSenderId === 'string' ? rawSenderId : null;
        return { txId: txIdUuid, latestMessageAt, latestSenderId };
      })
      .catch(() => null); // per-thread failure = skip that thread's dot
  };

  return Promise.all(txs.map(fetchOne))
    .then(results => {
      const map = {};
      results.forEach(r => {
        if (r && r.txId && r.latestMessageAt) {
          map[r.txId] = {
            latestMessageAt: r.latestMessageAt,
            latestSenderId: r.latestSenderId,
          };
        }
      });
      dispatch(fetchLatestMessagesSuccess(map));
      return map;
    })
    .catch(e => {
      dispatch(fetchLatestMessagesError(storableError(e)));
      return {};
    });
};
